import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Key,
  CheckCircle,
  QrCode,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../../components/loader/Loader";
import Modal from "../../../../components/modal/Modal";
import { Dropdown } from "../../../../components/dropdown/Dropdown";
import {
  getAllAccessAssignments,
  getAllAccessZones,
  revokeAccessAssignment,
  approveAccessAssignment,
  getAccessAssignmentQRCode,
  getAllVisitors,
} from "../../../../service/securityService";
import {
  AccessAssignment,
  SecuritySearchFilters,
  PaginationParams,
  AccessZone,
} from "../../../../types/security_types";
import AccessAssignmentForm from "./AccessAssignmentForm";
import { useOrgUsersStore } from "../../../../store/store";

const AccessAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<AccessAssignment[]>([]);
  const [zones, setZones] = useState<AccessZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [visitors, setVisitors] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  const [selectedAssignment, setSelectedAssignment] =
    useState<AccessAssignment | null>(null);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 20,
  });
  const fetchZones = async () => {
    try {
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;

      const data = await getAllAccessZones(filters);
      setZones(data);
    } catch (error: any) {
      console.error("Error fetching access zones:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to load access zones",
      );
    }
  };
  const fetchVisitors = async () => {
    try {
      const res = await getAllVisitors(undefined, { limit: 500, page: 1 });
      const visitorsData = res.data || [];
      setVisitors(
        visitorsData.map((visitor: any) => {
          const id = visitor._id || visitor.id;
          const name = visitor.fullName || visitor.name;
          return {
            value: String(id),
            label: name || String(id),
          };
        }),
      );
    } catch (error) {
      console.error("Error fetching visitors:", error);
    }
  };
  useEffect(() => {
    fetchZones();
    fetchAssignments();
    fetchOrgUsers();
    fetchVisitors();
  }, [searchQuery, selectedRegion, selectedPremises, statusFilter, pagination]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;
      if (statusFilter !== "ALL") filters.status = statusFilter;

      const response = await getAllAccessAssignments(filters, pagination);
      setAssignments(response.data || []);
    } catch (error: any) {
      console.error("Error fetching access assignments:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to load access assignments",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedAssignment(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (assignment: AccessAssignment) => {
    setSelectedAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const handleApprove = async (assignment: AccessAssignment) => {
    const id = assignment._id || assignment.id;
    if (!id) return;
    try {
      await approveAccessAssignment(String(id));
      toast.success("Assignment approved");
      fetchAssignments();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to approve assignment",
      );
    }
  };

  const handleQr = async (assignment: AccessAssignment) => {
    const id = assignment._id || assignment.id;
    if (!id) return;
    try {
      const res = await getAccessAssignmentQRCode(String(id));
      const code = res?.qrCode || "";
      if (code) {
        await navigator.clipboard.writeText(code);
        toast.success("QR payload copied to clipboard");
      } else {
        toast.info("No QR payload for this assignment");
      }
    } catch {
      toast.error("Could not load QR data");
    }
  };

  const handleRevoke = async (assignment: AccessAssignment) => {
    if (
      !window.confirm(
        `Are you sure you want to revoke access for "${assignment.userName || assignment.userType}"?`,
      )
    ) {
      return;
    }

    try {
      const id = assignment._id || assignment.id;
      if (!id) return;
      await revokeAccessAssignment(id);
      toast.success("Access revoked successfully");
      fetchAssignments();
    } catch (error: any) {
      console.error("Error revoking access:", error);
      toast.error(
        error.response?.data?.resMessage || "Failed to revoke access",
      );
    }
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedAssignment(null);
    fetchAssignments();
  };

  const getUserVisitorVendorName = (assignment: AccessAssignment) => {
    // Prefer explicit userName if provided
    if (assignment.userName && assignment.userName.trim()) {
      return assignment.userName;
    }

    if (assignment.userType === "User") {
      const user: any =
        orgUsersData.find((u: any) => u._id === assignment.userId) || {};
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      return (
        fullName ||
        user.name ||
        assignment.userId ||
        assignment._id ||
        assignment.id ||
        ""
      );
    }

    if (assignment.userType === "Visitor") {
      const visitor = visitors.find(
        (v) => v.value === (assignment.visitorId as unknown as string),
      );
      return (
        visitor?.label ||
        assignment.visitorId ||
        assignment._id ||
        assignment.id ||
        ""
      );
    }

    if (assignment.userType === "Vendor") {
      return (
        assignment.vendorId || assignment._id || assignment.id || "Unknown"
      );
    }

    // Fallback for any other/unknown type
    return (
      assignment.userId ||
      assignment.visitorId ||
      assignment.vendorId ||
      assignment._id ||
      assignment.id ||
      "Unknown"
    );
  };

  const getStatusBadge = (assignment: AccessAssignment) => {
    const now = new Date();
    const start = new Date(assignment.startDateTime);
    const end = new Date(assignment.endDateTime);

    if (assignment.status === "Pending") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-900">
          Pending approval
        </span>
      );
    }

    if (assignment.status === "Revoked") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
          Revoked
        </span>
      );
    }

    if (now < start) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          Scheduled
        </span>
      );
    }

    if (now > end) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
          Expired
        </span>
      );
    }

    return (
      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
        Active
      </span>
    );
  };

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Key className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Access Assignments
              </h1>
              <p className="text-gray-600">
                Manage user, visitor, and vendor access to zones
              </p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Assign Access
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search assignments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div className="min-w-[150px]">
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
          <div className="min-w-[150px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
            >
              <option value="ALL">All Status</option>
              <option value="Pending">Pending approval</option>
              <option value="Active">Active</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
              <option value="Revoked">Revoked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : assignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <Key className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">
              No access assignments found
            </p>
            <p className="text-sm">
              {searchQuery || selectedRegion !== "ALL Regions"
                ? "Try adjusting your search or filters"
                : "Create your first access assignment"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    User/Visitor/Vendor
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Zone
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Access Type
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Valid Period
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="py-3 px-4 font-semibold text-gray-700 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr
                    key={assignment._id || assignment.id}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {`${assignment.userType} - ${getUserVisitorVendorName(assignment)}`}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {assignment.zoneName ||
                        zones.find(
                          (zone) =>
                            String(zone._id || zone.id) ===
                            String(assignment.zoneId || assignment.zone || ""),
                        )?.zoneName ||
                        "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {assignment.accessType}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="text-xs">
                          {new Date(
                            assignment.startDateTime,
                          ).toLocaleDateString()}{" "}
                          -{" "}
                          {new Date(
                            assignment.endDateTime,
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(assignment)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {assignment.status === "Pending" && (
                          <button
                            onClick={() => handleApprove(assignment)}
                            className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Approve"
                            type="button"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        {assignment.accessType === "Temporary" && (
                          <button
                            onClick={() => handleQr(assignment)}
                            className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copy QR payload"
                            type="button"
                          >
                            <QrCode className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(assignment)}
                          className="p-2 text-[#0fa9a6] hover:bg-[#EAFBFA] rounded-lg transition-colors"
                          title="Edit"
                          type="button"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        {assignment.status !== "Revoked" && (
                          <button
                            onClick={() => handleRevoke(assignment)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Revoke"
                            type="button"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedAssignment(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <AccessAssignmentForm
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsCreateModalOpen(false);
              setSelectedAssignment(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsCreateModalOpen(false);
              setSelectedAssignment(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="accessAssignmentForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Assign
          </button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedAssignment(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <AccessAssignmentForm
            assignment={selectedAssignment || undefined}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedAssignment(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedAssignment(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="accessAssignmentForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Update
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AccessAssignments;
